import React from "react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/Card";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import {
  MessageCircleMore,
  Image,
  Dices,
  CircleFadingArrowUp,
  CalendarHeart,
} from "lucide-react";
import { clsx } from "clsx";
import { PartnerFeatureEnum } from "@/__generated__/types";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { RouteId } from "@/routes/getRoute";

const ICON_STYLE = {
  width: 25,
  height: 25,
  color: "var(--icon)",
};

export interface FeatureConfig {
  id: string;
  label: string;
  flow: string;
  feature?: PartnerFeatureEnum;
  icon: React.ReactNode;
  class?: string;
  showBadge: boolean;
  route?: RouteId;
}

export const GetStarted = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { navToRoute } = useProtectedNavigation();
  const { hasOrgFeature } = useOrgPermissions();

  const features: FeatureConfig[] = [
    {
      id: "bulk-message",
      label: t("communityTools.getStarted.bulkMessage.label"),
      flow: t("communityTools.getStarted.bulkMessage.flow"),
      class:
        "bg-orange-50 border-orange-200 text-orange-600 hover:bg-orange-100/70 hover:text-orange-600 hover:border-orange-300",
      feature: PartnerFeatureEnum.BulkMessaging,
      icon: (
        <MessageCircleMore className="h-[25px] w-[25px] text-orange-600 [&_svg:not([class*='size-'])]:size-4" />
      ),
      showBadge: true,
      route: "createBulkMessage",
    },
    {
      id: "automated-game",
      label: t("communityTools.getStarted.scheduledLiveEvent.label"),
      flow: t("communityTools.getStarted.scheduledLiveEvent.flow"),
      class:
        "bg-green-50 border-green-200 text-green-600 hover:bg-green-100/70 hover:text-green-600 hover:border-green-300",
      feature: PartnerFeatureEnum.Games,
      icon: (
        <Dices className="h-[25px] w-[25px] text-green-600 [&_svg:not([class*='size-'])]:size-4" />
      ),
      showBadge: true,
      route: "editGame",
    },
    {
      id: "competitor-match",
      label: t("communityTools.getStarted.competitorMatch.label"),
      flow: t("communityTools.getStarted.competitorMatch.flow"),
      class:
        "bg-sky-50 border-sky-200 text-sky-600 hover:bg-sky-100/70 hover:text-sky-600 hover:border-sky-300",
      icon: (
        <Dices className="h-[25px] w-[25px] text-sky-600 [&_svg:not([class*='size-'])]:size-4" />
      ),
      showBadge: false,
    },
    {
      id: "live-event",
      label: t("communityTools.getStarted.liveEvent.label"),
      flow: t("communityTools.getStarted.liveEvent.flow"),
      class:
        "bg-purple-50 border-purple-200 text-purple-600 hover:bg-purple-100/70 hover:text-purple-600 hover:border-purple-300",
      icon: (
        <CalendarHeart className="h-[25px] w-[25px] text-purple-600 [&_svg:not([class*='size-'])]:size-4" />
      ),
      showBadge: true,
      route: "editLiveEvent",
    },
    {
      id: "dab",
      label: t("communityTools.getStarted.dab.label"),
      flow: t("communityTools.getStarted.dab.flow"),
      icon: (
        <CircleFadingArrowUp
          className="h-[25px] w-[25px] rotate-180 text-sky-600 [&_svg:not([class*='size-'])]:size-4"
          style={ICON_STYLE}
        />
      ),
      showBadge: false,
    },
    {
      id: "feed-post",
      label: t("communityTools.getStarted.feedPost.label"),
      flow: t("communityTools.getStarted.feedPost.flow"),
      icon: (
        <Image
          className="h-[25px] w-[25px] text-sky-600 [&_svg:not([class*='size-'])]:size-4"
          style={ICON_STYLE}
        />
      ),
      showBadge: false,
    },
  ];

  return (
    <div className={className}>
      <Card>
        <CardHeader>
          <CardTitle className="font-dm-sans text-[24px] leading-[1.5] font-semibold tracking-normal text-card-foreground">
            {t("communityTools.getStarted.title")}
          </CardTitle>
          <CardDescription className="text-sm leading-[1.5] font-normal tracking-normal text-card-foreground">
            {t("communityTools.getStarted.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-2 sm:px-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {features
              .filter((config) => config.showBadge)
              .map((config) => {
                return (
                  <div key={config.id} className="w-full">
                    <Button
                      variant="outline"
                      onClick={() => config.route && navToRoute(config.route)}
                      className={clsx(
                        "group flex h-auto min-h-[47px] w-full flex-row items-center justify-between gap-2 p-2 sm:p-[12px_16px]",
                        config.class,
                      )}
                      aria-label={config.label}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                        {config.icon}
                        <span
                          className={clsx(
                            "line-clamp-2 flex-1 text-left font-normal break-words",
                          )}
                        >
                          {config.label}
                        </span>
                      </div>
                      {config.feature &&
                        hasOrgFeature(config.feature) === false && (
                          <Badge variant="gradient">
                            {t("global.upgrade")}
                          </Badge>
                        )}
                    </Button>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
